/**
 * GenericClimateSnapshot — /project/:id/climate-snapshot
 * Climate & Circularity Snapshot with 3-layer architecture:
 *   A. Climate layer — baseline, project, annual/lifetime reduction, uncertainty
 *   B. Circularity layer — waste diverted, biogas, electricity, digestate, score
 *   C. Integrity layer — Calculation Basis, Protocol, Crediting Pathway, status, confidence
 *
 * SOURCE OF TRUTH: All values from structured backend outputs via useProjectWorkspace.
 * No local recomputation of emissions math, circularity score, or percentage values.
 */
import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { RefreshCw, TrendingDown, AlertCircle, Recycle, Shield, Info, CheckCircle2, XCircle, AlertTriangle, Database } from "lucide-react";
import { useProjectWorkspace } from "@/hooks/useProjectWorkspace";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
import {
  CredibilityStatusBanner, MethodologyPanel, OutputClassificationTag,
  CarbonReadinessPanel, DisclosureNotice,
} from "@/components/CredibilityLayer";

export default function GenericClimateSnapshot() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    loading, isEmpty, journey, baseline, projectScenario, reduction, coBenefits,
    factors, sectorLabel, org, calcVersion, methodology, sector, projectState,
    circularityOutputs, climateOutputs, financialOutputs, dataQualityOutputs,
    calculationBasis, protocol, creditingPathway,
    vrsScore, vrsBand, scope2BaselineSource, scope2Basis,
    scopeOutputs,
    verificationStatus, verificationStatusLabel, verificationVvbLabel,
  } = useProjectWorkspace(id);

  // Verification tier from CDE
  const verReport = projectState?.report || {};
  const verLevel  = verReport.verification_level || "preliminary";
  const verBadge  = verReport.verification_badge || { label: "Preliminary", color: "amber" };
  const VER_BADGE_CLS = {
    amber:   "bg-amber-50 border-amber-200 text-amber-700",
    blue:    "bg-blue-50 border-blue-200 text-blue-700",
    teal:    "bg-teal-100 border-teal-300 text-teal-800",
    emerald: "bg-emerald-100 border-emerald-300 text-emerald-800",
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  // ── All core emission values from structured backend outputs ────────────────
  // baseline.tco2e_yr, projectScenario.tco2e_yr, reduction.tco2e_yr|total_tco2e_yr
  // reduction.reduction_pct, reduction.lifetime_tco2e — all from single journey source
  const baseVal       = baseline?.tco2e_yr ?? null;
  const projVal       = projectScenario?.tco2e_yr ?? null;
  const redVal        = reduction?.tco2e_yr ?? reduction?.total_tco2e_yr ?? null;
  const reductionPct  = reduction?.reduction_pct ?? null;
  const lifetimeTco2e = reduction?.lifetime_tco2e ?? null;
  const maxVal        = baseVal || 1;

  // Reconciliation check: reduction = baseline - project (within rounding tolerance)
  const computedReduction = (baseVal != null && projVal != null) ? baseVal - projVal : null;
  const reconciles = (redVal != null && computedReduction != null)
    ? Math.abs(redVal - computedReduction) < 0.1   // ±0.1 tCO2e tolerance
    : null;

  // A calculation is only valid when it produced non-zero emissions output.
  const hasValidCalc = !isEmpty && (baseVal != null && baseVal > 0);

  // ── Circularity — all from structured circularityOutputs (backend single source) ─
  const co_s           = circularityOutputs || {};
  const feedstockDiv   = Number(co_s.waste_diverted_tonnes || coBenefits?.feedstock_diverted_t_yr || 0);
  const biogasM3       = Number(co_s.biogas_produced_m3   || (projectScenario?.interventions?.captured_biogas_m3_yr) || 0);
  const elecMwh        = Number(co_s.electricity_generated_mwh || (coBenefits?.electricity_kwh_yr || 0) / 1000);
  const digestateT     = Number(co_s.digestate_output_tonnes || coBenefits?.digestate_t_yr || 0);
  const digestateRev   = Number(co_s.digestate_revenue_usd_yr || coBenefits?.digestate_revenue_usd_yr || 0);
  const energyPathway  = coBenefits?.energy_use_pathway || "";
  const circScore      = co_s.circularity_score || 0;
  const circLevel      = co_s.circularity_level || "LOW";
  const circColor      = circLevel === "HIGH"   ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                       : circLevel === "MEDIUM" ? "bg-teal-50 border-teal-200 text-teal-700"
                       : "bg-amber-50 border-amber-200 text-amber-700";
  const circBar        = circLevel === "HIGH" ? "bg-emerald-500" : circLevel === "MEDIUM" ? "bg-teal-500" : "bg-amber-400";
  const scorePct       = Math.min(100, circScore);

  // ── Integrity layer data ──────────────────────────────────────────────────
  const dq         = dataQualityOutputs || {};
  const confScore  = dq.confidence_score || (journey?.data_confidence?.data_confidence_score) || 0;
  const confLevel  = dq.confidence_level || (journey?.data_confidence?.label) || "Low";
  const compLevel  = projectState?.completeness?.level || "Early";
  const compScore  = projectState?.completeness?.score || 0;
  const verLabel   = verReport.verification_label || "Calculated Estimate — Pending Independent Verification";
  const calcBasis  = calculationBasis || "IPCC 2006 Guidelines";
  const protocolId = protocol || methodology || "DCI-BIOGAS-V1.0";
  const creditPw   = creditingPathway !== null && creditingPathway !== undefined ? creditingPathway : "Not Assigned";

  // ── Helpers ────────────────────────────────────────────────────────────────
  const factorEntries = Object.entries(factors || {}).slice(0, 6);

  // ── Institutional factor display metadata ─────────────────────────────────
  // Enriches raw factor_used data with classification, precise source, and institutional notes.
  const FACTOR_DISPLAY = [
    {
      key: "doc_by_waste_type",
      label: "DOC — Degradable Organic Carbon",
      displayUnit: "tC/tonne",
      classification: "Default",
      source: "IPCC 2006 Guidelines, Vol.5 Table 2.4",
      note: "Default factor for selected waste type. Site-specific lab analysis (BMP test) required for T1 data and VVB submission.",
    },
    {
      key: "mcf_by_disposal",
      label: "MCF — Methane Correction Factor",
      displayUnit: null,
      classification: "Assumed",
      source: "IPCC 2006 Guidelines, Vol.5 Table 3.2",
      note: "Assumed from baseline disposal characterization: shallow open dump (MCF = 0.4). Confirmed via site survey.",
    },
    {
      key: "gwp_ch4",
      label: "GWP CH₄ — Global Warming Potential",
      displayUnit: "CO₂e/CH₄",
      classification: "Methodology Basis",
      source: "IPCC AR4 (2007) — CDM/VCS legacy methodology basis",
      note: "AR4 = 25 used for methodology compatibility. Not the latest scientific value (AR5 = 28, AR6 = 27.2). See GWP Basis Disclosure below.",
    },
    {
      key: "f_ch4_fraction",
      label: "F — CH₄ Fraction in Landfill Gas",
      displayUnit: null,
      classification: "Default",
      source: "IPCC 2006 Guidelines, Vol.5 — default value applied in absence of site-specific measurement",
      note: null,
    },
    {
      key: "doc_f",
      label: "DOCf — Fraction of DOC Dissimilated",
      displayUnit: null,
      classification: "Default",
      source: "IPCC 2006 Guidelines, Vol.5 — default value applied in absence of site-specific measurement",
      note: null,
    },
    {
      key: "displacement_factor",
      label: "Displacement EF — Diesel Generator",
      displayUnit: "kgCO₂e/kWh",
      classification: "Default",
      source: "IPCC 2006, Vol.2 — diesel combustion (2.68 kgCO₂e/L ÷ 3.5 kWh/L)",
      note: "Applied because project displaces diesel generator electricity. Update if facility source changes.",
    },
    {
      key: "electrical_efficiency",
      label: "Electrical Conversion Efficiency",
      displayUnit: "fraction",
      classification: "Default",
      source: "Conservative default for gas engine (30%). Site commissioning test yields T1 data.",
      note: null,
    },
  ];

  // ── Uncertainty contributors ────────────────────────────────────────────
  const UNCERTAINTY_CONTRIBUTORS = [
    { label: "Biogas yield per tonne of waste",             level: "High" },
    { label: "DOC fraction (waste characterization)",       level: "Medium" },
    { label: "Baseline disposal characterization (MCF)",    level: "Medium" },
    { label: "Capture efficiency measurement",              level: "Medium" },
    { label: "Default factor reliance (F, DOCf)",           level: "Low" },
  ];

  // ── Derived values needed for reconciliation and VRS ──────────────────────
  const methaneAvoidance     = reduction?.methane_avoidance_tco2e_yr ?? 0;
  const electricityDisplaced = reduction?.electricity_displaced_tco2e_yr ?? 0;
  const gwpBasis             = journey?.input_snapshot?.gwp_standard?.toUpperCase() || "AR4";
  const gwpCh4Value          = { AR4: 25, AR5: 28, AR6: "27.2" }[gwpBasis] || 25;
  const baselineConfirmed    = baseline?.confirmation_status !== "assumed_default";

  // ── Multi-scope baseline reconciliation (CRITICAL — resolves scope comparison) ─
  // Scope 1 baseline = uncontrolled methane; Scope 2 baseline = fossil energy used
  // Total baseline = what WOULD happen without the project (both scopes)
  // Total project  = what DOES happen with the project (residual methane; zero fossil energy)
  const scope1Baseline   = baseVal ?? 0;
  const scope2Baseline   = electricityDisplaced;           // energy that project now avoids
  const totalBaseline    = scope1Baseline + scope2Baseline; // 7858.9 for Oko-Oba
  const scope1Project    = projVal ?? 0;
  const scope2Project    = 0;                               // biogas replaces fossil — zero residual
  const totalProject     = scope1Project + scope2Project;   // 1227.6
  const netReduction     = redVal ?? (totalBaseline - totalProject);  // must equal total_tco2e_yr
  const multiScopePct    = totalBaseline > 0
    ? Math.round(netReduction / totalBaseline * 1000) / 10
    : 0;                                                    // 84.4% for Oko-Oba

  // ── VRS — sourced from canonical backend (project_state.vrs) ──────────────
  // No client-side recomputation. vrsBand and vrsBandColor are display-only derivations.
  const vrsBandColor = vrsBand === "Pre-Verification Ready" ? "bg-emerald-100 text-emerald-800 border-emerald-300"
    : vrsBand === "Validation-Ready" ? "bg-teal-100 text-teal-800 border-teal-300"
    : vrsBand === "Developing"       ? "bg-amber-100 text-amber-800 border-amber-300"
    : "bg-slate-100 text-slate-500 border-slate-200";
  const hasElecDisplacement  = electricityDisplaced > 0;
  const hasDigestate         = digestateT > 0;

  const SCOPE_COVERAGE = [
    {
      scope: "Scope 1",
      category: "Waste methane avoidance",
      boundary: "Waste intake gate → biogas utilisation",
      status: methaneAvoidance > 0 ? "Available" : "Partial",
      basis: "Calculated — IPCC 2006 FOD model",
      value: methaneAvoidance > 0 ? `${Number(methaneAvoidance).toLocaleString()} tCO₂e/yr` : "—",
      included: true,
    },
    {
      scope: "Scope 1",
      category: "Residual emissions (fugitive loss)",
      boundary: "Within project plant boundary",
      status: projVal != null ? "Available" : "Partial",
      basis: "Calculated — default fugitive loss rate (3%)",
      value: projVal != null ? `${Number(projVal).toLocaleString()} tCO₂e/yr residual` : "—",
      included: true,
    },
    {
      scope: "Scope 2",
      category: "Fossil electricity displacement",
      boundary: "Facility energy boundary",
      status: hasElecDisplacement ? "Available" : "Not Included",
      basis: scope2Basis || (hasElecDisplacement ? "Calculated — diesel generator EF (IPCC 2006 Vol.2)" : "No electricity generation pathway"),
      value: hasElecDisplacement ? `${Number(electricityDisplaced).toLocaleString()} tCO₂e/yr` : "—",
      included: hasElecDisplacement,
      baselineSource: scope2BaselineSource,
    },
    {
      scope: "Scope 3",
      category: "Waste transport & logistics",
      boundary: "Upstream feedstock supply chain",
      status: "Not Included",
      basis: "No transport activity data — out of current boundary",
      value: "—",
      included: false,
    },
    {
      scope: "Scope 3",
      category: "Fertilizer substitution (digestate)",
      boundary: "Downstream beneficial use",
      status: hasDigestate ? "Partial" : "Not Included",
      basis: hasDigestate
        ? `Physical: ${Number(digestateT).toLocaleString()} t/yr — GHG displacement not yet calculated`
        : "No digestate offtake confirmed",
      value: hasDigestate ? `${Number(digestateT).toLocaleString()} t/yr (physical only)` : "—",
      included: false,
    },
    {
      scope: "Scope 3",
      category: "Other upstream/downstream",
      boundary: "Equipment mfg., worker transport, etc.",
      status: "Not Included",
      basis: "Outside project accounting boundary — not applicable to this model",
      value: "—",
      included: false,
    },
  ];

  // ── Data readiness by module (scores derived from data coverage, completeness, evidence strength) ─
  const evCovPct = dq.evidence_coverage_pct || 0;
  const hasCriticalMissing = (dq.missing_critical_count || 0) > 0;

  // Score derivation: weight data coverage (40%) + evidence completeness (40%) + factor tier (20%)
  const wasteDataScore = Math.round(evCovPct * 0.40 + Math.min(compScore, 100) * 0.40 + Math.min((dq.tier_multiplier ?? 0.75) * 100, 100) * 0.20);
  const energyDataScore = 60;  // grid factor default (IEA), efficiency default — medium reliability
  const cobeneDataScore = feedstockDiv > 0 ? 80 : 20;

  const DATA_READINESS = [
    {
      module: "Waste Data",
      items: "Feedstock volume, waste characterization, baseline disposal",
      status: evCovPct >= 70 ? "Available" : evCovPct >= 30 ? "Partial" : "Gap",
      score: wasteDataScore,
      band: wasteDataScore >= 70 ? "High" : wasteDataScore >= 40 ? "Medium" : "Low",
      gap: evCovPct >= 70
        ? "Evidence on file. Strengthen with lab-confirmed waste composition data for T1."
        : hasCriticalMissing
        ? "Critical evidence gaps. Site survey and weighbridge records required."
        : "Evidence partially complete. Lab-confirmed composition data improves confidence.",
    },
    {
      module: "Energy Baseline",
      items: "Displacement source, grid/diesel EF, electrical efficiency",
      status: hasElecDisplacement ? "Available" : "Partial",
      score: energyDataScore,
      band: "Medium",
      gap: "IEA default grid factor. Diesel displacement EF from IPCC standard. On-site meter data would upgrade to T1.",
    },
    {
      module: "Transport & Logistics",
      items: "Feedstock transport, digestate delivery logistics",
      status: "Not Included",
      score: null,
      band: "N/A",
      gap: "Out of boundary. Scope 3 inclusion requires confirmed transport activity data.",
    },
    {
      module: "Co-benefits",
      items: "Waste diverted, biogas volume, electricity generated, digestate",
      status: feedstockDiv > 0 ? "Available" : "Partial",
      score: cobeneDataScore,
      band: feedstockDiv > 0 ? "High" : "Low",
      gap: feedstockDiv > 0
        ? "Physical co-benefit outputs calculated. Digestate GHG displacement (Scope 3) not yet quantified."
        : "Run calculation to generate co-benefit outputs.",
    },
  ];

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <PageHeader
            label="PROJECT WORKSPACE"
            title="Climate & Circularity Snapshot"
            subtitle={`Baseline vs project emission profile · ${calcVersion ? `v${calcVersion.number}` : "No calculation yet"} · ${protocolId}`}
            backUrl={`/project/${id}/workspace`}
            backLabel={sectorLabel}
          />
        </div>
        {!isEmpty && (
          <div className="flex items-center gap-2 flex-shrink-0 ml-4 mt-1">
            {/* Verification badge — conservative, never implies "Independently Verified" unless formally initiated */}
            <span className={`text-xs font-bold px-2.5 py-1.5 rounded-full border ${
              verificationStatus === "IN_VALIDATION"
                ? "bg-blue-100 text-blue-700 border-blue-200"
                : "bg-slate-100 text-slate-500 border-slate-200"
            }`}
              data-testid="snapshot-verification-badge"
              title="Verification requires formal VVB engagement — see Evidence Vault for details">
              {verificationStatus === "IN_VALIDATION"
                ? "Verification: In Progress"
                : "Verification: Not Initiated"}
            </span>
            {hasValidCalc && (
              <span className={`text-xs font-bold px-2.5 py-1.5 rounded-full border ${vrsBandColor}`}
                data-testid="vrs-badge" title={`Verification Readiness Score: ${vrsScore}/100`}>
                VRS {vrsScore} — {vrsBand}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Credibility Status Banner */}
      {!isEmpty && projectState && (
        <CredibilityStatusBanner projectState={projectState} />
      )}

      {/* KPI strip — totals from canonical scope_outputs (Scope 1+2 combined) */}
      {hasValidCalc && (() => {
        const so = scopeOutputs;
        const totalBase = so?.total_baseline_tco2e_yr  ?? baseVal ?? 0;
        const totalProj = so?.total_project_tco2e_yr   ?? projVal ?? 0;
        const totalRed  = so?.net_total_reduction_tco2e_yr ?? redVal ?? 0;
        const totalPct  = so?.total_reduction_pct       ?? multiScopePct ?? 0;
        return (
          <div className="grid grid-cols-4 gap-4">
            <MetricCard label="Total Baseline (S1+S2)" value={totalBase ? `${totalBase.toFixed(1)}` : "—"} unit="tCO₂e/yr" sub={<OutputClassificationTag verificationLevel={verLevel} />} color="text-red-600" />
            <MetricCard label="Total Project (S1+S2)"  value={totalProj ? `${totalProj.toFixed(1)}` : "—"}  unit="tCO₂e/yr" sub={<OutputClassificationTag verificationLevel={verLevel} />} color="text-teal-700" />
            <MetricCard label="Net Total Reduction"     value={totalRed  ? `${totalRed.toFixed(1)}`  : "—"}  unit="tCO₂e/yr" sub={<OutputClassificationTag verificationLevel={verLevel} />} color="text-emerald-700" />
            <MetricCard label="Total Reduction %"       value={totalPct  ? `${totalPct}%` : "—"} sub={<OutputClassificationTag verificationLevel={verLevel} />} color="text-teal-700" />
          </div>
        );
      })()}

      {/* ── SCOPE COVERAGE PANEL (NEW) ─────────────────────────────────── */}
      {hasValidCalc && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="scope-coverage-panel">
          <div className="px-5 py-3 bg-[#0F172A] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield size={13} className="text-teal-400" />
              <p className="text-xs font-bold text-white uppercase tracking-wide">Scope Coverage Assessment</p>
            </div>
            <span className="text-xs text-slate-400 italic">Validation-Ready Model — What is calculated, assumed, and excluded</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  {["Scope", "Category", "Boundary", "Status", "Basis", "Value"].map(h => (
                    <th key={h} className="text-left text-xs text-slate-400 px-4 py-2.5 font-semibold uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SCOPE_COVERAGE.map((row, i) => (
                  <tr key={i} className={`border-b border-slate-50 last:border-0 ${!row.included ? "opacity-70" : ""}`}>
                    <td className="px-4 py-2.5">
                      <span className={`font-bold text-xs px-2 py-0.5 rounded ${
                        row.scope === "Scope 1" ? "bg-red-100 text-red-700" :
                        row.scope === "Scope 2" ? "bg-blue-100 text-blue-700" :
                        "bg-slate-100 text-slate-500"
                      }`}>{row.scope}</span>
                    </td>
                    <td className="px-4 py-2.5 font-semibold text-slate-700">{row.category}</td>
                    <td className="px-4 py-2.5 text-slate-400 max-w-[180px]">{row.boundary}</td>
                    <td className="px-4 py-2.5">
                      <span className={`font-bold px-2 py-0.5 rounded text-xs flex items-center gap-1 w-fit ${
                        row.status === "Available"    ? "bg-emerald-100 text-emerald-700" :
                        row.status === "Partial"      ? "bg-amber-100 text-amber-700" :
                        "bg-slate-100 text-slate-500"
                      }`}>
                        {row.status === "Available"    && <CheckCircle2 size={9} />}
                        {row.status === "Partial"      && <AlertTriangle size={9} />}
                        {row.status === "Not Included" && <XCircle size={9} />}
                        {row.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-500 max-w-[200px]">
                      {row.basis}
                      {row.baselineSource && (
                        <p className="text-xs text-amber-600 italic mt-0.5">Baseline: {row.baselineSource}</p>
                      )}
                    </td>
                    <td className="px-4 py-2.5 font-bold text-slate-900 whitespace-nowrap">{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-5 py-2.5 bg-amber-50 border-t border-amber-100">
            <p className="text-xs text-amber-700">
              <strong>Boundary note:</strong> This assessment covers Scope 1 waste methane and Scope 2 electricity displacement only.
              Scope 3 (supply chain, downstream beneficial use) is excluded — mark fields as included only when site-specific data is confirmed.
              This is a Validation-Ready Model, not a full verified GHG inventory.
            </p>
          </div>
        </div>
      )}

      {/* ── STANDALONE BOUNDARY DISCLOSURE (PART 5) ─────────────────────── */}
      {hasValidCalc && (
        <div className="bg-white border-2 border-slate-200 rounded-xl overflow-hidden" data-testid="boundary-disclosure">
          <div className="px-5 py-3 bg-slate-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Info size={13} className="text-slate-300" />
              <p className="text-xs font-bold text-white uppercase tracking-wide">Accounting Boundary & Methodology</p>
            </div>
            <span className="text-xs text-slate-400 italic">Validation-Ready Model — not a verified GHG inventory</span>
          </div>
          <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
            <div>
              <p className="font-bold text-emerald-700 uppercase tracking-wide mb-2">Included</p>
              {[
                "Scope 1: Waste methane avoidance (IPCC FOD)",
                "Scope 1: Residual fugitive emissions",
                "Scope 2: Fossil electricity displacement",
                scope2BaselineSource ? `  ↳ Baseline: ${scope2BaselineSource}` : "  ↳ Baseline: assumed (source not specified)",
              ].map(s => (
                <div key={s} className="flex items-start gap-1.5 mb-1">
                  <CheckCircle2 size={9} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">{s}</span>
                </div>
              ))}
            </div>
            <div>
              <p className="font-bold text-red-600 uppercase tracking-wide mb-2">Excluded</p>
              {[
                "Scope 3: Waste transport & logistics",
                "Scope 3: Fertilizer substitution",
                "Scope 3: Equipment lifecycle",
                "All other upstream/downstream",
              ].map(s => (
                <div key={s} className="flex items-start gap-1.5 mb-1">
                  <XCircle size={9} className="text-slate-400 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-500">{s}</span>
                </div>
              ))}
            </div>
            <div>
              <p className="font-bold text-slate-600 uppercase tracking-wide mb-2">Methodology</p>
              <div className="space-y-1">
                <div><span className="text-slate-400">Calculation Basis:</span> <span className="font-semibold text-slate-700">{calcBasis}</span></div>
                <div><span className="text-slate-400">Protocol:</span> <span className="font-semibold font-mono text-slate-700">{protocolId}</span></div>
                <div><span className="text-slate-400">GWP Standard:</span> <span className="font-semibold text-slate-700">IPCC {gwpBasis} (CH₄ = {gwpCh4Value})</span></div>
                <div><span className="text-slate-400">GWP Note:</span> <span className="text-slate-500 italic">CDM/VCS legacy basis</span></div>
              </div>
            </div>
            <div>
              <p className="font-bold text-slate-600 uppercase tracking-wide mb-2">Model Status</p>
              <div className="space-y-2">
                <div className={`text-xs font-bold px-2 py-1.5 rounded-lg border ${vrsBandColor}`}>
                  VRS {vrsScore}/100 — {vrsBand}
                </div>
                <div className={`text-xs font-bold px-2 py-1.5 rounded-lg border bg-slate-100 text-slate-500 border-slate-200`}
                  data-testid="boundary-verification-status">
                  {verificationStatus === "IN_VALIDATION" ? "Verification: In Progress" : "Verification: Not Initiated"}
                </div>
                <p className="text-slate-500 text-xs leading-relaxed">
                  This is a <strong>Validation-Ready Model</strong>. Outputs are methodology-aligned and auditable.
                  Not a third-party verified GHG inventory.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {isEmpty ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
          <AlertCircle size={20} className="text-amber-400 mx-auto mb-3" />
          <p className="text-sm font-bold text-amber-800">No calculation yet</p>
          <p className="text-xs text-amber-600 mb-4">Enter project inputs to generate the climate snapshot.</p>
          <button onClick={() => navigate(`/project/${id}/input-data`)}
            className="px-5 py-2 bg-amber-600 text-white text-xs font-bold rounded-xl hover:bg-amber-700">
            Enter Inputs
          </button>
        </div>
      ) : !hasValidCalc ? (
        <div className="bg-white border border-amber-200 rounded-xl p-8 text-center">
          <AlertCircle size={20} className="text-amber-500 mx-auto mb-3" />
          <p className="text-sm font-bold text-amber-800">Insufficient data to compute emissions</p>
          <p className="text-xs text-amber-600 mb-4">Enter valid inputs (annual feedstock volume &#62; 0) and recalculate.</p>
          <button onClick={() => navigate(`/project/${id}/input-data`)}
            className="px-5 py-2 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700">
            Update Inputs
          </button>
        </div>
      ) : (
        <div className="space-y-5">

          {/* ── SECTION A: Climate Layer ─────────────────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

            {/* Left 2/3 */}
            <div className="lg:col-span-2 space-y-5">

              {/* A1: Emission Structure — Scope-by-Scope (Baseline / Project / Reduction) */}
              <SectionCard>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">A. Emission Structure — Scope by Scope</p>
                  <span className="text-xs text-slate-400 italic">Baseline / Project / Reduction per scope</span>
                </div>

                {(() => {
                  const so = scopeOutputs;
                  if (!so) return null;
                  const s1  = so.scope_1  || {};
                  const s2  = so.scope_2  || {};
                  const s3c = so.scope_3_categories || [];
                  const totalBase = so.total_baseline_tco2e_yr  || 0;
                  const totalProj = so.total_project_tco2e_yr   || 0;
                  const totalRed  = so.net_total_reduction_tco2e_yr || 0;
                  const totalPct  = so.total_reduction_pct || 0;

                  const maxForBars = Math.max(s1.baseline_tco2e_yr || 0, s2.baseline_tco2e_yr || 0, 1);

                  function ScopeRow({ label, value, color, max, note }) {
                    const pct = max > 0 && value != null ? Math.min((value / max) * 100, 100) : 0;
                    return (
                      <div className="mb-2">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-xs text-slate-600 w-32 flex-shrink-0">{label}</span>
                          <span className="text-xs font-bold text-slate-900 tabular-nums flex-shrink-0">
                            {value != null ? `${Number(value).toFixed(1)} tCO₂e/yr` : "—"}
                          </span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all duration-700 ${color}`} style={{ width: `${pct}%` }} />
                        </div>
                        {note && <p className="text-xs text-slate-400 italic mt-0.5">{note}</p>}
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-5">

                      {/* Scope 1 */}
                      <div>
                        <div className="flex items-center gap-2 mb-2.5">
                          <span className="text-xs font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded">SCOPE 1</span>
                          <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">Waste Methane Boundary</p>
                          <span className="text-xs text-slate-400 italic ml-auto">{s1.basis}</span>
                        </div>
                        <ScopeRow label="Baseline emissions"  value={s1.baseline_tco2e_yr}  color="bg-red-400"     max={s1.baseline_tco2e_yr || 1} />
                        <ScopeRow label="Project emissions"   value={s1.project_tco2e_yr}   color="bg-teal-500"    max={s1.baseline_tco2e_yr || 1} />
                        <ScopeRow label="Reduction"           value={s1.reduction_tco2e_yr} color="bg-emerald-500" max={s1.baseline_tco2e_yr || 1} />
                        <p className="text-xs text-slate-400 italic mt-1">
                          Boundary: {s1.boundary} · Reduction = {s1.baseline_tco2e_yr ? `${(s1.reduction_tco2e_yr / s1.baseline_tco2e_yr * 100).toFixed(1)}%` : "—"} of Scope 1 baseline
                        </p>
                      </div>

                      {/* Scope 2 */}
                      {s2.baseline_tco2e_yr > 0 && (
                        <div className="pt-4 border-t border-slate-100">
                          <div className="flex items-center gap-2 mb-2.5">
                            <span className="text-xs font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded">SCOPE 2</span>
                            <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">Energy Boundary</p>
                            <span className="text-xs text-slate-400 italic ml-auto">{s2.basis}</span>
                          </div>
                          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-1.5 mb-3">
                            <strong>Baseline energy source:</strong> {s2.baseline_source}
                          </div>
                          <ScopeRow label="Baseline emissions" value={s2.baseline_tco2e_yr}  color="bg-red-300"     max={s2.baseline_tco2e_yr || 1} />
                          <ScopeRow label="Project emissions"  value={s2.project_tco2e_yr}   color="bg-teal-400"    max={s2.baseline_tco2e_yr || 1}
                            note={s2.project_note} />
                          <ScopeRow label="Reduction"          value={s2.reduction_tco2e_yr} color="bg-blue-400"    max={s2.baseline_tco2e_yr || 1} />
                          <p className="text-xs text-slate-400 italic mt-1">
                            Boundary: {s2.boundary} · Reduction = 100% of Scope 2 baseline (full fossil energy replacement)
                          </p>
                        </div>
                      )}

                      {/* Scope 3 */}
                      <div className="pt-4 border-t border-slate-100">
                        <div className="flex items-center gap-2 mb-2.5">
                          <span className="text-xs font-bold bg-slate-100 text-slate-500 px-2 py-0.5 rounded">SCOPE 3</span>
                          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Categories</p>
                        </div>
                        <div className="space-y-2">
                          {s3c.map(cat => (
                            <div key={cat.category} className="flex items-start justify-between text-xs bg-slate-50 rounded-lg px-3 py-2">
                              <div className="flex-1 min-w-0 mr-3">
                                <p className="font-semibold text-slate-600">{cat.category}</p>
                                <p className="text-slate-400 italic mt-0.5">{cat.note}</p>
                              </div>
                              <span className={`font-bold px-2 py-0.5 rounded flex-shrink-0 ${
                                cat.status === "Partial" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-400"
                              }`}>{cat.status}</span>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-slate-400 italic mt-2">Scope 3 values not calculated — no data available to prevent fabrication.</p>
                      </div>

                      {/* Output Tiers — CORE / EXTENDED / CONDITIONAL */}
                      <div className="pt-4 border-t-2 border-slate-200">
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Output Tiers</p>
                        <div className="space-y-2">
                          {/* CORE: Scope 1 methane avoidance */}
                          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-bold bg-emerald-600 text-white px-2 py-0.5 rounded">CORE</span>
                                <p className="text-xs font-bold text-emerald-800">Scope 1 — Methane Avoidance</p>
                              </div>
                              <p className="text-sm font-bold text-emerald-800 tabular-nums">
                                {so.scope_1?.reduction_tco2e_yr > 0 ? `${Number(so.scope_1.reduction_tco2e_yr).toLocaleString()} tCO₂e/yr` : "—"}
                              </p>
                            </div>
                            <p className="text-xs text-emerald-600 mt-0.5">Primary methane avoidance within waste boundary.</p>
                          </div>
                          {/* EXTENDED: Scope 1 + Scope 2 */}
                          <div className="bg-teal-50 border border-teal-200 rounded-xl px-4 py-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-bold bg-teal-600 text-white px-2 py-0.5 rounded">EXTENDED</span>
                                <p className="text-xs font-bold text-teal-800">Scope 1+2 — Total GHG Impact</p>
                              </div>
                              <p className="text-sm font-bold text-teal-800 tabular-nums">
                                {totalRed > 0 ? `${Number(totalRed).toLocaleString()} tCO₂e/yr` : "—"}
                              </p>
                            </div>
                            <p className="text-xs text-teal-600 mt-0.5">Includes electricity displacement. {totalPct}% of total baseline ({Number(totalBase).toLocaleString()} tCO₂e/yr).</p>
                          </div>
                          {/* CONDITIONAL: Scope 3 */}
                          <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 opacity-70">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-bold bg-slate-400 text-white px-2 py-0.5 rounded">CONDITIONAL</span>
                                <p className="text-xs font-bold text-slate-600">Scope 3 — Co-benefit Displacement</p>
                              </div>
                              <p className="text-xs font-semibold text-slate-400 italic">Not Calculated</p>
                            </div>
                            <p className="text-xs text-slate-400 mt-0.5">Requires additional data and verification.</p>
                          </div>
                        </div>
                        <p className="text-xs text-slate-400 italic mt-2 text-center">
                          Lifetime (Extended): ~{Number(lifetimeTco2e || 0).toLocaleString()} tCO₂e · {reduction?.project_life_years || "—"} yr
                        </p>
                        <p className="text-xs text-emerald-700 font-semibold mt-1 text-center">
                          Reconciliation: {Number(totalBase).toFixed(1)} − {Number(totalProj).toFixed(1)} = {Number(totalRed).toFixed(1)} tCO₂e/yr ✓
                        </p>
                      </div>

                    </div>
                  );
                })()}
              </SectionCard>

              {/* A2: Scenario descriptions */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <SectionCard>
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Baseline Scenario</p>
                  <p className="text-xs text-slate-600 leading-relaxed">{baseline?.scenario || "Not computed."}</p>
                  {baseline?.parameters && (
                    <div className="mt-3 pt-3 border-t border-slate-100 space-y-0.5">
                      {Object.entries(baseline.parameters).slice(0, 4).map(([k, v]) => (
                        <div key={k} className="flex justify-between text-xs">
                          <span className="text-slate-400">{k.replace(/_/g," ")}</span>
                          <span className="font-semibold text-slate-600">{typeof v === "number" ? v.toLocaleString() : String(v)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </SectionCard>
                <div className="bg-white border border-slate-200 rounded-xl p-5">
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Project Scenario</p>
                  <p className="text-xs text-slate-600 leading-relaxed">{projectScenario?.scenario || "Not computed."}</p>
                  {reduction?.intensity_note && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <p className="text-xs text-teal-600">
                        Intensity: {reduction.intensity_tco2e_per_tonne || reduction.intensity_tco2e_per_kwp || "—"}
                        · {reduction.intensity_note}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* ── SECTION B: Circularity Layer ─────────────────────────── */}
              <div className={`border rounded-xl p-5 ${circColor}`} data-testid="circularity-panel">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Recycle size={14} className="text-current" />
                    <p className="text-xs font-bold uppercase tracking-wider">B. Circularity Layer</p>
                    <span className="text-xs bg-white/50 px-1.5 py-0.5 rounded text-slate-500">Weighted Score · Backend</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-16 bg-white/40 rounded-full overflow-hidden">
                      <div className={`h-full ${circBar} rounded-full`} style={{ width: `${scorePct.toFixed(0)}%` }} />
                    </div>
                    <span className="text-xs font-bold" data-testid="circularity-score">{circLevel}</span>
                    {circScore > 0 && <span className="text-xs text-slate-500">{circScore}/100</span>}
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="bg-white/60 rounded-xl p-3">
                    <p className="text-xs text-slate-400 mb-0.5">Waste Diverted</p>
                    <p className="text-sm font-bold text-slate-900">{feedstockDiv > 0 ? `${Number(feedstockDiv).toLocaleString()} t/yr` : "—"}</p>
                    <p className="text-xs text-slate-500 italic">100% of processed feedstock</p>
                  </div>
                  <div className="bg-white/60 rounded-xl p-3">
                    <p className="text-xs text-slate-400 mb-0.5">Biogas Produced</p>
                    <p className="text-sm font-bold text-slate-900">{biogasM3 > 0 ? `${Number(biogasM3).toLocaleString()} m³/yr` : "—"}</p>
                    <p className="text-xs text-slate-500 italic">Captured from digestion</p>
                  </div>
                  <div className="bg-white/60 rounded-xl p-3">
                    <p className="text-xs text-slate-400 mb-0.5">Electricity Generated</p>
                    <p className="text-sm font-bold text-slate-900">
                      {elecMwh > 0 ? `${Number(elecMwh).toLocaleString(undefined,{maximumFractionDigits:0})} MWh/yr`
                      : energyPathway === "flaring" ? "Flaring only" : "—"}
                    </p>
                    <p className="text-xs text-slate-500 italic">
                      {energyPathway === "electricity" ? "Grid-connected / captive"
                      : energyPathway === "chp" ? "Combined heat & power"
                      : energyPathway === "heat_only" ? "Thermal only"
                      : energyPathway === "flaring" ? "No energy recovery"
                      : ""}
                    </p>
                  </div>
                  <div className="bg-white/60 rounded-xl p-3">
                    <p className="text-xs text-slate-400 mb-0.5">Digestate Output</p>
                    <p className="text-sm font-bold text-slate-900">{digestateT > 0 ? `${Number(digestateT).toLocaleString()} t/yr` : "—"}</p>
                    <p className="text-xs text-slate-500 italic">
                      {digestateRev > 0 ? `Biofertilizer · $${Number(digestateRev).toLocaleString()}/yr` : "Biofertilizer (no revenue confirmed)"}
                    </p>
                  </div>
                  <div className="bg-white/60 rounded-xl p-3">
                    <p className="text-xs text-slate-400 mb-0.5">Diversion Rate</p>
                    <p className="text-sm font-bold text-slate-900">{feedstockDiv > 0 ? "100%" : "—"}</p>
                    <p className="text-xs text-slate-500 italic">
                      From {baseline?.parameters?.baseline_disposal?.replace(/_/g," ") || "baseline disposal"}
                    </p>
                  </div>
                  <div className="bg-white/60 rounded-xl p-3">
                    <p className="text-xs text-slate-400 mb-0.5">Circularity Score</p>
                    <p className={`text-sm font-bold ${circLevel === "HIGH" ? "text-emerald-700" : circLevel === "MEDIUM" ? "text-teal-700" : "text-amber-700"}`}>
                      {circScore > 0 ? `${circScore}/100 — ${circLevel}` : circLevel}
                    </p>
                    <p className="text-xs text-slate-500 italic">
                      {circLevel === "HIGH"   ? "Full loop: waste + energy + digestate"
                      : circLevel === "MEDIUM" ? "Partial loop: 2 of 3 recovery streams"
                      : "Basic: waste diversion only"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right 1/3 */}
            <div className="space-y-4">

              {/* Key Metrics */}
              <div className="bg-[#0F172A] rounded-xl p-5">
                <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Key Metrics</p>
                <div className="space-y-2">
                  {[
                    ["Total GHG Impact (Scope 1+2)", redVal != null && redVal > 0 ? `${Number(redVal).toFixed(1)} tCO₂e/yr` : "—", "text-teal-400"],
                    ["Lifetime Impact",              lifetimeTco2e > 0 ? `${Number(lifetimeTco2e).toLocaleString()} tCO₂e` : "—", "text-white"],
                    ["Methane Avoidance % (S1)",     reductionPct ? `${reductionPct}%` : "—", "text-emerald-400"],
                    ["3yr Cumulative Impact",         reduction?.cumulative_3yr_tco2e > 0 ? `${Number(reduction.cumulative_3yr_tco2e).toLocaleString()} tCO₂e` : "—", "text-slate-300"],
                  ].map(([l, v, c]) => (
                    <div key={l} className="bg-white/5 rounded-xl px-3 py-2.5">
                      <p className="text-xs text-slate-500">{l}</p>
                      <p className={`text-sm font-bold ${c}`}>{v}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-600 italic mt-2">
                  Scope 1+2 combined. "Methane Avoidance %" is Scope 1 only vs Scope 1 baseline.
                </p>
              </div>

              {/* Factor Credibility Panel — institutional-grade disclosure */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-5 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Emission Factors Used</p>
                  <span className="text-xs text-slate-400 italic">Source · Classification · Basis</span>
                </div>
                <div className="divide-y divide-slate-50">
                  {FACTOR_DISPLAY.map(f => {
                    const factorData = factors?.[f.key];
                    if (!factorData) return null;
                    const val = typeof factorData.value === 'number' ? factorData.value : factorData.value;
                    const unit = f.displayUnit || factorData.unit || '';
                    return (
                      <div key={f.key} className="px-5 py-3">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p className="text-xs font-bold text-slate-700">{f.label}</p>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <span className="text-xs font-bold text-slate-900">{val}</span>
                            {unit && unit !== '--' && <span className="text-xs text-slate-500">{unit}</span>}
                            <span className={`text-xs font-semibold px-1.5 py-0.5 rounded text-xs ${
                              f.classification === 'Measured'          ? 'bg-emerald-100 text-emerald-700' :
                              f.classification === 'User-selected'     ? 'bg-blue-100 text-blue-700' :
                              f.classification === 'Assumed'           ? 'bg-amber-100 text-amber-700' :
                              f.classification === 'Methodology Basis' ? 'bg-purple-100 text-purple-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>{f.classification}</span>
                          </div>
                        </div>
                        <p className="text-xs text-slate-400">{f.source || factorData.source}</p>
                        {f.note && <p className="text-xs text-amber-600 italic mt-0.5">{f.note}</p>}
                      </div>
                    );
                  }).filter(Boolean)}
                </div>
                {/* GWP explicit disclosure */}
                <div className="px-5 py-3 bg-amber-50 border-t border-amber-100">
                  <p className="text-xs font-bold text-amber-800 mb-1">GWP Basis Disclosure</p>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    Selected GWP basis for this model: <strong>IPCC AR4 (CH₄ = 25)</strong>, retained for methodology compatibility
                    with CDM/VCS legacy standards. This differs from newer reporting frameworks: AR5 = 28, AR6 = 27.2.
                    Changing GWP standard would alter baseline and reduction figures but not the underlying physical calculation.
                  </p>
                </div>
              </div>

              {/* Uncertainty — with contributor breakdown */}
              {journey?.uncertainty_pct != null && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-amber-600 uppercase tracking-wider font-semibold">Model Uncertainty</p>
                    <p className="text-lg font-bold text-amber-800">±{journey.uncertainty_pct}%</p>
                  </div>
                  <p className="text-xs text-amber-700 leading-relaxed mb-3">
                    {journey.uncertainty_note || `Indicative combined uncertainty at project development stage.`}
                  </p>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-1">Primary Contributors</p>
                    {UNCERTAINTY_CONTRIBUTORS.map(u => (
                      <div key={u.label} className="flex items-center justify-between text-xs">
                        <span className="text-amber-700">{u.label}</span>
                        <span className={`font-bold px-1.5 py-0.5 rounded text-xs ${
                          u.level === 'High'   ? 'bg-red-100 text-red-700' :
                          u.level === 'Medium' ? 'bg-amber-200 text-amber-800' :
                          'bg-amber-100 text-amber-600'
                        }`}>{u.level}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-amber-600 italic mt-2">
                    Uncertainty narrows to ±10% with 12+ months of metered production data and independent factor measurement.
                  </p>
                </div>
              )}

              {/* Data Readiness Panel — with numeric scores and scoring logic */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="data-readiness-panel">
                <div className="px-5 py-3 bg-slate-50 border-b border-slate-100">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Data Readiness</p>
                    <span className="text-xs text-slate-400 italic">By module</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Scores derived from data coverage (40%), completeness (40%), factor reliability (20%).</p>
                </div>
                <div className="divide-y divide-slate-50">
                  {DATA_READINESS.map(d => (
                    <div key={d.module} className="px-5 py-3">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <p className="text-xs font-bold text-slate-700">{d.module}</p>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {d.score != null && (
                            <div className="h-1.5 w-12 bg-slate-100 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${
                                d.score >= 70 ? "bg-emerald-500" : d.score >= 40 ? "bg-amber-400" : "bg-red-400"
                              }`} style={{ width: `${d.score}%` }} />
                            </div>
                          )}
                          <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                            d.band === "High"   ? "bg-emerald-100 text-emerald-700" :
                            d.band === "Medium" ? "bg-amber-100 text-amber-700" :
                            d.band === "N/A"    ? "bg-slate-100 text-slate-400" :
                            "bg-red-100 text-red-700"
                          }`}>{d.score != null ? `${d.score}/100` : "N/A"} {d.band !== "N/A" ? `· ${d.band}` : ""}</span>
                        </div>
                      </div>
                      <p className="text-xs text-slate-400 mb-1">{d.items}</p>
                      <div className="flex items-start gap-1.5">
                        {d.status === "Available"    && <CheckCircle2 size={9} className="text-emerald-500 flex-shrink-0 mt-0.5" />}
                        {d.status === "Partial"      && <AlertTriangle size={9} className="text-amber-500 flex-shrink-0 mt-0.5" />}
                        {d.status === "Not Included" && <XCircle size={9} className="text-slate-400 flex-shrink-0 mt-0.5" />}
                        {d.status === "Gap"          && <XCircle size={9} className="text-red-400 flex-shrink-0 mt-0.5" />}
                        <p className="text-xs text-slate-500 italic">{d.gap}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ── SECTION C: Integrity Layer ─────────────────────────────────── */}
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="integrity-layer">
            <div className="flex items-center gap-2 mb-4">
              <Shield size={13} className="text-teal-600" />
              <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">C. Integrity Layer</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4">
              {[
                { label: "Calculation Basis", value: calcBasis, sub: "Scientific standard" },
                { label: "Protocol",          value: protocolId, sub: "Internal digital MRV", mono: true },
                { label: "Crediting Pathway", value: creditPw, sub: creditPw === "Not Assigned" ? "No external registry selected" : "External pathway", amber: creditPw === "Not Assigned" },
                { label: "Verification",      value: verificationStatus === "IN_VALIDATION" ? "Verification: In Progress" : "Verification: Not Initiated", sub: "VVB engagement required — see Evidence Vault" },
                { label: "Data Completeness", value: `${compLevel} (${compScore}%)`, sub: "CDE assessment" },
                { label: "Confidence",        value: `${confScore}/100 ${confLevel}`, sub: "Evidence-weighted" },
                { label: "VRS",               value: `${vrsScore}/100`, sub: vrsBand, highlight: true, vrsBand },
              ].map(({ label, value, sub, mono, amber, highlight, vrsBand: band }) => (
                <div key={label}>
                  <p className="text-xs text-slate-400 mb-0.5">{label}</p>
                  <p className={`text-xs font-bold ${
                    highlight ? (band === "Pre-Verification Ready" ? "text-emerald-700" : band === "Validation-Ready" ? "text-teal-700" : "text-amber-700")
                    : amber ? "text-amber-600"
                    : "text-slate-800"
                  } ${mono ? "font-mono" : ""}`}>{value}</p>
                  <p className="text-xs text-slate-400 italic mt-0.5">{sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Credibility panels */}
      {!isEmpty && (
        <div className="space-y-4 mt-4">
          <MethodologyPanel
            methodology={methodology}
            calculationBasis={calculationBasis}
            protocol={protocol}
            creditingPathway={creditingPathway}
            sector={sector}
            compact={true}
          />
          <CarbonReadinessPanel projectState={projectState} journey={journey} />
          <DisclosureNotice />
        </div>
      )}
    </div>
  );
}
